// ABOUTME: Container for all modal dialogs used in AdminFamilyDetail
// ABOUTME: Centralizes modal instantiation and prop passing for cleaner orchestrator

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import EditFamilyInfoModal from './modals/EditFamilyInfoModal';
import EditBehaviorModal from './modals/EditBehaviorModal';
import AddAdultModal from './modals/AddAdultModal';
import AddChildModal from './modals/AddChildModal';
import DeleteFamilyDialog from './modals/DeleteFamilyDialog';
import ManageSubscriptionModal from './modals/ManageSubscriptionModal';
import RoleEditModal from '../RoleEditModal';
import AdminGoalCreationModal from '../AdminGoalCreationModal';
import { roleAPI, adminAPI } from '../../services/api';
import { BehaviorEventEntry } from '../BehaviorEventsTable';
import { RoleSuggestion, FamilyDeletionSummary } from '../../types/api';
import type {
  FamilyMember,
  FamilyDetailData,
  SubscriptionInfo,
  AddAdultFormState,
  AddChildFormState,
  EditFamilyFormState,
  DeleteFormState,
} from './types';

interface ModalsContainerProps {
  // Edit Family Modal
  editDialogOpen: boolean;
  setEditDialogOpen: (open: boolean) => void;
  editForm: EditFamilyFormState;
  setEditForm: (form: EditFamilyFormState) => void;
  handleEditSubmit: () => Promise<void>;

  // Edit Behavior Modal
  editBehaviorDialogOpen: boolean;
  setEditBehaviorDialogOpen: (open: boolean) => void;
  selectedBehavior: BehaviorEventEntry | null;
  setSelectedBehavior: (behavior: BehaviorEventEntry | null) => void;
  fetchBehaviors: () => void;

  // Add Adult Modal
  addAdultModalOpen: boolean;
  setAddAdultModalOpen: (open: boolean) => void;
  addAdultForm: AddAdultFormState;
  setAddAdultForm: (form: AddAdultFormState) => void;
  showPasswords: { password: boolean; confirmPassword: boolean };
  setShowPasswords: (show: { password: boolean; confirmPassword: boolean }) => void;
  togglePasswordVisibility: (field: 'password' | 'confirmPassword') => void;
  roleSuggestions: RoleSuggestion[];
  loadingRoles: boolean;
  addFamilyMemberError: string | null;
  handleAddAdult: () => Promise<void>;

  // Add Child Modal
  addChildModalOpen: boolean;
  setAddChildModalOpen: (open: boolean) => void;
  addChildForm: AddChildFormState;
  setAddChildForm: (form: AddChildFormState) => void;
  handleAddChild: () => Promise<void>;

  // Delete Family Dialog
  deleteDialogOpen: boolean;
  handleDeleteCancel: () => void;
  handleDeleteAcknowledge: () => void;
  family: FamilyDetailData;
  deleteForm: DeleteFormState;
  setDeleteForm: (form: DeleteFormState) => void;
  deleteError: string | null;
  deletionSummary: FamilyDeletionSummary | null;
  handleDeleteSubmit: () => Promise<void>;

  // Role Edit Modal
  roleEditModalOpen: boolean;
  setRoleEditModalOpen: (open: boolean) => void;
  editingMemberRole: { userId: number; currentRole: string; familyId: number } | null;
  setEditingMemberRole: (role: { userId: number; currentRole: string; familyId: number } | null) => void;
  fetchFamilyDetail: () => Promise<void>;
  setError: (error: string | null) => void;

  // Admin Goal Modal
  adminGoalModalOpen: boolean;
  setAdminGoalModalOpen: (open: boolean) => void;
  handleAdminGoalSave: () => void;
  showSnackbar: (message: string, severity: 'success' | 'error') => void;

  // Remove Member Dialog
  removeMemberDialogOpen: boolean;
  setRemoveMemberDialogOpen: (open: boolean) => void;
  memberToRemove: FamilyMember | null;
  confirmRemoveFamilyMember: () => Promise<void>;

  // Manage Subscription Modal
  manageSubscriptionModalOpen: boolean;
  setManageSubscriptionModalOpen: (open: boolean) => void;
  subscriptionInfo: SubscriptionInfo | null;
  onSubscriptionChange: () => void;
}

const ModalsContainer: React.FC<ModalsContainerProps> = ({
  editDialogOpen,
  setEditDialogOpen,
  editForm,
  setEditForm,
  handleEditSubmit,
  editBehaviorDialogOpen,
  setEditBehaviorDialogOpen,
  selectedBehavior,
  setSelectedBehavior,
  fetchBehaviors,
  addAdultModalOpen,
  setAddAdultModalOpen,
  addAdultForm,
  setAddAdultForm,
  showPasswords,
  setShowPasswords,
  togglePasswordVisibility,
  roleSuggestions,
  loadingRoles,
  addFamilyMemberError,
  handleAddAdult,
  addChildModalOpen,
  setAddChildModalOpen,
  addChildForm,
  setAddChildForm,
  handleAddChild,
  deleteDialogOpen,
  handleDeleteCancel,
  handleDeleteAcknowledge,
  family,
  deleteForm,
  setDeleteForm,
  deleteError,
  deletionSummary,
  handleDeleteSubmit,
  roleEditModalOpen,
  setRoleEditModalOpen,
  editingMemberRole,
  setEditingMemberRole,
  fetchFamilyDetail,
  setError,
  adminGoalModalOpen,
  setAdminGoalModalOpen,
  handleAdminGoalSave,
  showSnackbar,
  removeMemberDialogOpen,
  setRemoveMemberDialogOpen,
  memberToRemove,
  confirmRemoveFamilyMember,
  manageSubscriptionModalOpen,
  setManageSubscriptionModalOpen,
  subscriptionInfo,
  onSubscriptionChange
}) => {
  return (
    <>
      <EditFamilyInfoModal
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        editForm={editForm}
        onChange={setEditForm}
        onSubmit={handleEditSubmit}
      />

      <EditBehaviorModal
        open={editBehaviorDialogOpen}
        onClose={() => setEditBehaviorDialogOpen(false)}
        behavior={selectedBehavior}
        onChange={setSelectedBehavior}
        onSubmit={async () => {
          if (selectedBehavior) {
            try {
              await adminAPI.updateAdminBehavior(selectedBehavior.behaviorId, {
                behaviorType: selectedBehavior.behaviorType,
                notes: selectedBehavior.notes,
                childId: selectedBehavior.childId
              });
              setEditBehaviorDialogOpen(false);
              fetchBehaviors();
            } catch (error) {
              console.error('Error updating behavior:', error);
            }
          }
        }}
      />

      <AddAdultModal
        open={addAdultModalOpen}
        onClose={() => {
          setAddAdultModalOpen(false);
          setAddAdultForm({
            firstName: '',
            lastName: '',
            email: '',
            role: 'Parent',
            isPrimaryCaregiver: false,
            invitationMethod: 'email',
            password: '',
            confirmPassword: '',
            mantra: '',
            familyLoveQuestion: '',
            familyNeedQuestion: ''
          });
          setShowPasswords({ password: false, confirmPassword: false });
        }}
        form={addAdultForm}
        onChange={setAddAdultForm}
        showPasswords={showPasswords}
        onTogglePasswordVisibility={togglePasswordVisibility}
        roleSuggestions={roleSuggestions}
        loadingRoles={loadingRoles}
        error={addFamilyMemberError}
        onSubmit={handleAddAdult}
      />

      <AddChildModal
        open={addChildModalOpen}
        onClose={() => {
          setAddChildModalOpen(false);
          setAddChildForm({
            name: '',
            aliases: [],
            birthYear: 0,
            birthMonth: '',
            gender: '',
            mantra: '',
            familyLoveQuestion: '',
            familyNeedQuestion: ''
          });
        }}
        form={addChildForm}
        onChange={setAddChildForm}
        onSubmit={handleAddChild}
      />

      <DeleteFamilyDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onCancel={handleDeleteCancel}
        onAcknowledge={handleDeleteAcknowledge}
        familyName={family.name}
        form={deleteForm}
        onChange={setDeleteForm}
        error={deleteError}
        deletionSummary={deletionSummary}
        onSubmit={handleDeleteSubmit}
      />

      {editingMemberRole && (
        <RoleEditModal
          open={roleEditModalOpen}
          onClose={() => {
            setRoleEditModalOpen(false);
            setEditingMemberRole(null);
          }}
          onSave={async (newRole: string, isPrimaryCaregiver: boolean) => {
            if (editingMemberRole && family) {
              try {
                await roleAPI.adminUpdateFamilyRole(
                  editingMemberRole.userId,
                  editingMemberRole.familyId,
                  newRole,
                  isPrimaryCaregiver
                );

                await fetchFamilyDetail();
              } catch (err) {
                console.error('Error updating family role:', err);
                setError('Failed to update family role');
              }
            }
          }}
          currentRole={editingMemberRole.currentRole}
          userId={editingMemberRole.userId}
          familyId={editingMemberRole.familyId}
          isAdmin={true}
          isPrimaryCaregiver={family.adults.find(adult => adult.id === editingMemberRole.userId)?.role === 'Owner'}
        />
      )}

      <AdminGoalCreationModal
        open={adminGoalModalOpen}
        onClose={() => setAdminGoalModalOpen(false)}
        onSave={handleAdminGoalSave}
        familyId={family?.id || 0}
        showSnackbar={showSnackbar}
      >
        {family?.children || []}
      </AdminGoalCreationModal>

      <Dialog
        open={removeMemberDialogOpen}
        onClose={() => setRemoveMemberDialogOpen(false)}
        aria-labelledby="remove-member-dialog-title"
        aria-describedby="remove-member-dialog-description"
      >
        <DialogTitle id="remove-member-dialog-title">
          Remove Family Member
        </DialogTitle>
        <DialogContent>
          <Typography id="remove-member-dialog-description">
            Are you sure you want to remove <strong>{memberToRemove?.firstName} {memberToRemove?.lastName}</strong> from this family?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action cannot be undone. The member will lose access to the family and all related data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveMemberDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveFamilyMember}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Remove Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Subscription Modal */}
      <ManageSubscriptionModal
        open={manageSubscriptionModalOpen}
        onClose={() => setManageSubscriptionModalOpen(false)}
        familyId={family.id}
        familyName={family.name}
        subscriptionInfo={subscriptionInfo}
        onActionComplete={onSubscriptionChange}
      />
    </>
  );
};

export default ModalsContainer;
